import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { CreditCard, Bank, CheckCircle, XCircle, Clock, CurrencyDollar, ArrowsClockwise, ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { VerificationQueueInner } from "./AdminPaymentVerificationPage";

export default function PaymentPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [history, setHistory] = useState([]);
  const [pending, setPending] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [reconcileAdmissionNo, setReconcileAdmissionNo] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => {
    // Deep link support — /payments?tab=verification jumps straight to the queue.
    const p = new URLSearchParams(window.location.search).get("tab");
    return ["overview", "approvals", "verification"].includes(p) ? p : "overview";
  });
  useEffect(() => {
    const p = new URLSearchParams(location.search).get("tab");
    if (p && ["overview", "approvals", "verification"].includes(p) && p !== tab) setTab(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [programmes, setProgrammes] = useState([]);

  const [reconcileForm, setReconcileForm] = useState({
    user_id: "", programme_id: "", amount: "", reference: "", notes: "",
    currency: "USD", discount: "", payment_kind: "full",
    balance_override: "", payment_status_override: "",
  });
  const [exchangeRate, setExchangeRate] = useState(null);

  const isAdmin = ["super_admin", "admin", "finance"].includes(user?.role);
  const userId = user?.id || user?._id;

  useEffect(() => {
    api.get("/payments/history").then(r => { setHistory(r.data); setLoading(false); }).catch(() => setLoading(false));
    api.get("/exchange-rate").then(r => setExchangeRate(r.data?.usd_to_ngn)).catch(() => {});
    if (isAdmin) {
      api.get("/enrollments/my").then(r => setEnrollments(r.data)).catch(() => {});
      api.get("/payments/pending-approvals").then(r => setPending(r.data)).catch(() => {});
      api.get("/users/directory").then(r => setStudents(r.data.filter(u => u.role === "student"))).catch(() => {});
      api.get("/programmes").then(r => setProgrammes(r.data)).catch(() => {});
    } else {
      api.get("/enrollments/my").then(r => setEnrollments(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  // Round 32 — pull admission_number when both student + programme are
  // chosen in the reconcile dialog, so admin sees the human-friendly id.
  useEffect(() => {
    if (!isAdmin) return;
    const { user_id, programme_id } = reconcileForm;
    if (!user_id || !programme_id) { setReconcileAdmissionNo(""); return; }
    api.get(`/admin/enrollment/find?user_id=${user_id}&programme_id=${programme_id}`)
      .then(r => setReconcileAdmissionNo(r.data?.admission_number || ""))
      .catch(() => setReconcileAdmissionNo(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconcileForm.user_id, reconcileForm.programme_id]);

  const myEnrollments = isAdmin ? [] : enrollments;

  const fmtNGN = (usd) => exchangeRate ? ` (NGN ${Math.round(usd * exchangeRate).toLocaleString()})` : "";

  const handleDeletePayment = async (txId) => {
    if (!window.confirm("Are you sure you want to delete this payment entry? This action cannot be undone.")) return;
    try {
      await api.delete(`/payments/${txId}`);
      toast.success("Payment entry deleted");
      const { data } = await api.get("/payments/history");
      setHistory(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const submitTransfer = null; // unused after Round 29 unified Pay flow
  void submitTransfer;

  const approvePayment = async (txId) => {
    try {
      await api.post(`/payments/${txId}/approve`);
      toast.success("Payment approved!");
      const [h, p] = await Promise.all([api.get("/payments/history"), api.get("/payments/pending-approvals")]);
      setHistory(h.data);
      setPending(p.data);
    } catch (e) { toast.error("Failed to approve"); }
  };

  const rejectPayment = async (txId) => {
    try {
      await api.post(`/payments/${txId}/reject`, { reason: "Payment not verified" });
      toast.success("Payment rejected");
      const [h, p] = await Promise.all([api.get("/payments/history"), api.get("/payments/pending-approvals")]);
      setHistory(h.data);
      setPending(p.data);
    } catch (e) { toast.error("Failed to reject"); }
  };

  const submitReconcile = async () => {
    if (!reconcileForm.user_id || !reconcileForm.programme_id || !reconcileForm.amount) {
      toast.error("Student, programme and amount required"); return;
    }
    try {
      // Send all reconciliation knobs so admin can record currency, discount,
      // explicit balance + status overrides for full/part/discounted flows.
      const payload = {
        user_id: reconcileForm.user_id,
        programme_id: reconcileForm.programme_id,
        amount: parseFloat(reconcileForm.amount) || 0,
        currency: reconcileForm.currency || "USD",
        discount: parseFloat(reconcileForm.discount || 0),
        payment_kind: reconcileForm.payment_kind || "full",
        reference: reconcileForm.reference,
        notes: reconcileForm.notes,
      };
      if (reconcileForm.balance_override !== "" && reconcileForm.balance_override !== null) {
        payload.balance_override = parseFloat(reconcileForm.balance_override);
      }
      if (reconcileForm.payment_status_override) {
        payload.payment_status_override = reconcileForm.payment_status_override;
      }
      const { data } = await api.post("/payments/reconcile", payload);
      if (data.message === "duplicate_detected") {
        toast.warning(`This student has a pending payment notification ($${data.pending_amount}, Ref: ${data.pending_reference || "N/A"}). Please go to Pending Approvals to approve it instead.`, { duration: 8000 });
        setReconcileOpen(false);
        setTab("approvals");
        return;
      }
      toast.success(`Reconciled — payment_status: ${data.payment_status}, balance: $${data.balance}`);
      setReconcileOpen(false);
      setReconcileForm({ user_id: "", programme_id: "", amount: "", reference: "", notes: "", currency: "USD", discount: "", payment_kind: "full", balance_override: "", payment_status_override: "" });
      const { data: hist } = await api.get("/payments/history");
      setHistory(hist);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reconcile"); }
  };

  const statusBadge = (status) => {
    const colors = {
      completed: "bg-green-100 text-green-800", paid: "bg-green-100 text-green-800",
      pending: "bg-amber-100 text-amber-800", pending_approval: "bg-amber-100 text-amber-800",
      initiated: "bg-blue-100 text-blue-800",
      rejected: "bg-red-100 text-red-700", expired: "bg-slate-100 text-slate-500",
    };
    return colors[status] || "bg-slate-100 text-slate-500";
  };

  const typeLabel = (type) => {
    const labels = { stripe: "Card (Stripe)", transfer: "Bank Transfer", manual: "Manual", reconciliation: "Reconciled" };
    return labels[type] || type;
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="payment-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Finance</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Payments
            </h1>
          </div>
          <div className="flex gap-2">
            {!isAdmin && myEnrollments.length > 0 && (
              <Link to={`/pay/${myEnrollments[0].programme_id}`}>
                <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2 text-sm" data-testid="student-pay-now-btn">
                  <Bank size={16} /> Pay Now
                </Button>
              </Link>
            )}
            {isAdmin && (
              <Dialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-none gap-2 text-sm" data-testid="reconcile-payment-btn">
                    <ArrowsClockwise size={16} /> Reconcile Payment
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md rounded-none">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Reconcile Payment</DialogTitle>
                  </DialogHeader>
                  <p className="text-xs text-slate-500 mb-3">Record a payment made outside the system (e.g. direct bank deposit). Choose <b>Full</b> / <b>Part</b> / <b>Discounted</b> — admin is the final authority on the resulting balance.</p>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm">Student</Label>
                      <Select value={reconcileForm.user_id} onValueChange={v => setReconcileForm({ ...reconcileForm, user_id: v })}>
                        <SelectTrigger className="mt-1 rounded-none" data-testid="reconcile-student"><SelectValue placeholder="Select student" /></SelectTrigger>
                        <SelectContent>
                          {students.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.email})</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm">Programme</Label>
                      <Select value={reconcileForm.programme_id} onValueChange={v => setReconcileForm({ ...reconcileForm, programme_id: v })}>
                        <SelectTrigger className="mt-1 rounded-none" data-testid="reconcile-programme"><SelectValue placeholder="Select programme" /></SelectTrigger>
                        <SelectContent>
                          {programmes.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {reconcileForm.user_id && reconcileForm.programme_id && (
                        <p className="text-[11px] text-blue-900 mt-1" data-testid="reconcile-admission-no">
                          Admission No:&nbsp;
                          <b className="font-mono">{reconcileAdmissionNo || "—"}</b>
                        </p>
                      )}
                    </div>

                    {/* Payment kind — Full / Part / Discounted */}
                    <div>
                      <Label className="text-sm">Payment kind</Label>
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        {[
                          { k: "full", l: "Full" },
                          { k: "partial", l: "Part" },
                          { k: "discounted", l: "Discounted" },
                        ].map(o => (
                          <button
                            key={o.k}
                            type="button"
                            onClick={() => setReconcileForm({ ...reconcileForm, payment_kind: o.k })}
                            data-testid={`reconcile-kind-${o.k}`}
                            className={`px-3 py-2 border-2 text-xs font-bold uppercase tracking-wider ${reconcileForm.payment_kind === o.k ? "border-blue-800 bg-blue-50 text-blue-900" : "border-slate-200 text-slate-600 hover:border-blue-300"}`}
                          >{o.l}</button>
                        ))}
                      </div>
                    </div>

                    {/* Currency + amount in one row */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-1">
                        <Label className="text-sm">Currency</Label>
                        <Select value={reconcileForm.currency} onValueChange={v => setReconcileForm({ ...reconcileForm, currency: v })}>
                          <SelectTrigger className="mt-1 rounded-none" data-testid="reconcile-currency"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD ($)</SelectItem>
                            <SelectItem value="NGN">NGN (₦)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-sm">Amount paid ({reconcileForm.currency})</Label>
                        <Input type="number" value={reconcileForm.amount} onChange={e => setReconcileForm({ ...reconcileForm, amount: e.target.value })} className="mt-1 rounded-none" placeholder="0.00" data-testid="reconcile-amount" />
                      </div>
                    </div>

                    {/* Discount value — required when payment_kind=discounted */}
                    {reconcileForm.payment_kind === "discounted" && (
                      <div>
                        <Label className="text-sm">Approved discount value (USD)</Label>
                        <Input type="number" value={reconcileForm.discount} onChange={e => setReconcileForm({ ...reconcileForm, discount: e.target.value })} className="mt-1 rounded-none" placeholder="e.g. 200" data-testid="reconcile-discount" />
                        <p className="text-[11px] text-slate-500 mt-1">Reduces the student's payable amount. Balance = (fee − discount) − amount paid.</p>
                      </div>
                    )}

                    <div>
                      <Label className="text-sm">Reference</Label>
                      <Input value={reconcileForm.reference} onChange={e => setReconcileForm({ ...reconcileForm, reference: e.target.value })} className="mt-1 rounded-none" placeholder="Bank reference number" data-testid="reconcile-reference" />
                    </div>

                    {/* Optional explicit overrides — admin authority */}
                    <details className="border border-slate-200 p-2">
                      <summary className="text-xs font-semibold text-slate-600 cursor-pointer">Admin overrides (advanced)</summary>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <Label className="text-[11px]">Balance override (USD)</Label>
                          <Input type="number" value={reconcileForm.balance_override} onChange={e => setReconcileForm({ ...reconcileForm, balance_override: e.target.value })} className="rounded-none" placeholder="auto" data-testid="reconcile-balance-override" />
                        </div>
                        <div>
                          <Label className="text-[11px]">Payment status</Label>
                          <Select value={reconcileForm.payment_status_override || "auto"} onValueChange={v => setReconcileForm({ ...reconcileForm, payment_status_override: v === "auto" ? "" : v })}>
                            <SelectTrigger className="rounded-none" data-testid="reconcile-status-override"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="auto">Auto (recommended)</SelectItem>
                              <SelectItem value="paid">paid</SelectItem>
                              <SelectItem value="partial">partial</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </details>

                    <div>
                      <Label className="text-sm">Notes</Label>
                      <Textarea value={reconcileForm.notes} onChange={e => setReconcileForm({ ...reconcileForm, notes: e.target.value })} className="mt-1 rounded-none" rows={2} data-testid="reconcile-notes" />
                    </div>
                    <Button onClick={submitReconcile} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="submit-reconcile-btn">
                      <CheckCircle size={16} /> Reconcile Payment
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Bank Account Details for Students */}
        {!isAdmin && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Bank Account Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card className="border border-slate-200 rounded-none" data-testid="bank-naira">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Bank size={18} className="text-blue-800" />
                    <p className="text-sm font-bold text-slate-900">Naira Account (NGN)</p>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Bank</span><span className="font-semibold text-slate-900">Providus Bank</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Account Number</span><span className="font-semibold text-slate-900 font-mono">1309466171</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Account Name</span><span className="font-semibold text-slate-900">School Builder's Lab</span></div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border border-slate-200 rounded-none" data-testid="bank-dollar">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <CurrencyDollar size={18} className="text-emerald-700" />
                    <p className="text-sm font-bold text-slate-900">Dollar Account (USD)</p>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Bank</span><span className="font-semibold text-slate-900">Providus Bank</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Account Number</span><span className="font-semibold text-slate-900 font-mono">1309470279</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Account Name</span><span className="font-semibold text-slate-900">School Builder's Lab</span></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Outstanding Fees for Students — each card links into the new
            unified /pay/:id flow (PayNowPage) so the student sees the same
            polished interface they see from the programme page. */}
        {!isAdmin && myEnrollments.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Outstanding Fees</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {myEnrollments.map(e => (
                <Link key={e.id} to={`/pay/${e.programme_id}`} data-testid={`outstanding-${e.id}`} className="block group">
                  <Card className={`border ${e.balance > 0 ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"} rounded-none transition-all group-hover:shadow-md`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-900 truncate">{e.programme_title || e.programme_id}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-600">
                            <span>Paid: ${e.amount_paid?.toFixed(2)}</span>
                            {e.balance > 0 ? (
                              <span className="text-red-600 font-medium">· Balance: ${e.balance?.toFixed(2)}</span>
                            ) : (
                              <span className="text-emerald-700 font-medium">· Fully paid</span>
                            )}
                          </div>
                        </div>
                        {e.balance > 0 && (
                          <div className="flex items-center gap-1 text-blue-800 font-semibold text-xs whitespace-nowrap">
                            Pay <ArrowRight size={12} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none bg-slate-100 mb-6">
            <TabsTrigger value="overview" className="rounded-none text-sm" data-testid="tab-overview">Payment History</TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="approvals" className="rounded-none text-sm" data-testid="tab-approvals">
                Pending Approvals {pending.length > 0 && <Badge className="ml-2 rounded-none text-xs bg-amber-600">{pending.length}</Badge>}
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="verification" className="rounded-none text-sm gap-1" data-testid="tab-verification">
                <ShieldCheck size={14} /> Verification Queue
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="overview">
            {loading ? (
              <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-slate-100 animate-pulse" />)}</div>
            ) : history.length === 0 ? (
              <div className="text-center py-16">
                <CreditCard size={48} className="text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No payment records</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payment-history-table">
                  <thead>
                    <tr className="bg-slate-950 text-white">
                      <th className="text-left px-4 py-3 font-medium">Date</th>
                      <th className="text-left px-3 py-3 font-medium">Type</th>
                      <th className="text-right px-3 py-3 font-medium">Amount</th>
                      <th className="text-center px-3 py-3 font-medium">Status</th>
                      {isAdmin && <th className="text-left px-3 py-3 font-medium">Reference</th>}
                      {user?.role === "super_admin" && <th className="px-2 py-3 font-medium"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((tx, i) => (
                      <tr key={tx.id} className={`border-b border-slate-100 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                        <td className="px-4 py-3">{new Date(tx.created_at).toLocaleDateString()}</td>
                        <td className="px-3 py-3">{typeLabel(tx.payment_type)}</td>
                        <td className="px-3 py-3 text-right font-medium">
                          {(() => {
                            // Round 33 — dual-currency display: original (raw)
                            // amount + currency, then USD equivalent + rate.
                            const usd = Number(tx.converted_amount_usd ?? tx.amount ?? 0);
                            const rawCur = (tx.raw_currency || tx.currency || "USD").toUpperCase();
                            const rawAmt = tx.raw_amount != null ? Number(tx.raw_amount) : Number(tx.amount_in_currency ?? tx.amount ?? 0);
                            const rate = Number(tx.exchange_rate_used || 0);
                            const isNgn = rawCur === "NGN";
                            if (isNgn && Math.abs(rawAmt - usd) > 0.01) {
                              return (
                                <div className="flex flex-col items-end">
                                  <span className="font-mono" data-testid={`amt-raw-${tx.id}`}>₦{rawAmt.toLocaleString()} NGN</span>
                                  <span className="text-[10px] text-emerald-700 font-mono" data-testid={`amt-usd-${tx.id}`}>→ ${usd.toFixed(2)} USD{rate ? ` (rate ${Number(rate).toLocaleString()})` : ""}</span>
                                </div>
                              );
                            }
                            return <span className="font-mono">${usd.toFixed(2)}<span className="text-[10px] text-slate-400 font-normal">{fmtNGN(usd)}</span></span>;
                          })()}
                        </td>
                        <td className="text-center px-3 py-3">
                          <Badge className={`rounded-none text-xs ${statusBadge(tx.payment_status || tx.status)}`}>
                            {(tx.payment_status || tx.status || "").replace("_", " ").toUpperCase()}
                          </Badge>
                        </td>
                        {isAdmin && <td className="px-3 py-3 text-xs text-slate-500">{tx.reference || tx.stripe_session_id?.slice(0, 12) || "-"}</td>}
                        {user?.role === "super_admin" && (
                          <td className="px-2 py-3">
                            <button onClick={() => handleDeletePayment(tx.id)} className="text-xs text-red-500 hover:text-red-700">Delete</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {isAdmin && (
            <TabsContent value="approvals">
              {pending.length === 0 ? (
                <div className="text-center py-16">
                  <Clock size={48} className="text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No pending approvals</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pending.map(tx => (
                    <Card key={tx.id} className="border border-amber-200 rounded-none" data-testid={`pending-${tx.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between flex-wrap gap-3">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{tx.user_name || tx.user_id}</p>
                            <p className="text-xs text-slate-500 mt-1">Amount: <span className="font-medium text-slate-900">${tx.amount?.toFixed(2)}</span></p>
                            <p className="text-xs text-slate-500">Reference: {tx.reference || "None"}</p>
                            {tx.metadata?.notes && <p className="text-xs text-slate-500">Notes: {tx.metadata.notes}</p>}
                            <p className="text-xs text-slate-400 mt-1">{new Date(tx.created_at).toLocaleString()}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => approvePayment(tx.id)} className="bg-green-700 text-white hover:bg-green-800 rounded-none gap-1 text-xs" data-testid={`approve-${tx.id}`}>
                              <CheckCircle size={14} /> Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => rejectPayment(tx.id)} className="rounded-none gap-1 text-xs border-red-300 text-red-600 hover:bg-red-50" data-testid={`reject-${tx.id}`}>
                              <XCircle size={14} /> Reject
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="verification" data-testid="verification-tab-content">
              {/* Unified Verification Queue — same controls as the legacy
                  /admin/payment-verification page (approve, reject, edit,
                  view-receipt, delete) without the duplicate Layout chrome. */}
              <div className="-mx-6 md:-mx-8 lg:-mx-12 -mt-2">
                <VerificationQueueInner />
              </div>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}
